import type {HermesState, NativeSessionDetail} from './types';
import {internalConversationKinds as internalDisplayKinds} from '../../src/customer-conversation';

export interface ConversationMessage {
  key: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: number | null;
  unprocessedCorrection: boolean;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

export function messageDisplayPrefix(state: Pick<HermesState, 'accountSettings'>): string | null {
  return state.accountSettings.status !== 'error' ? state.accountSettings.data?.messagePrefix?.prefix ?? null : null;
}

/** Remove runtime input headers from display text without changing the native records. */
export function userMessageDisplayText(text: string, prefix: string, options: {mergedSteer?: boolean; folded?: boolean} = {}): string {
  if (!prefix) throw new Error('The native message prefix is required for message display.');
  let visible = text;
  if (visible === prefix) visible = '';
  else if (visible.startsWith(`${prefix}\r\n`)) visible = visible.slice(prefix.length + 2);
  else if (visible.startsWith(`${prefix}\n`)) visible = visible.slice(prefix.length + 1);
  else if (options.folded && visible.startsWith(`${prefix} `)) visible = visible.slice(prefix.length + 1);
  // Hermes can merge several pending steering inputs into one display row.
  // Match whole header lines only; mentions within the user's prose stay visible.
  if (options.mergedSteer) visible = visible.split('\n')
    .filter(line => line !== prefix && line !== `${prefix}\r`).join('\n');
  return visible;
}

/** Select chat entries from Hermes's display projection without reinterpreting saved model content. */
export function conversationMessages(displayHistory: readonly Record<string, unknown>[], prefix: string,
  liveTurn: NativeSessionDetail['liveTurn'] = null): ConversationMessage[] {
  const project = (history: readonly Record<string, unknown>[], start = 0): ConversationMessage[] => history.flatMap((message, index) => {
    const role = message.role;
    if (role !== 'user' && role !== 'assistant') return [];
    const metadata = record(message.display_metadata);
    if (metadata?._eight_internal_notification === true ||
        typeof message.display_kind === 'string' && internalDisplayKinds.has(message.display_kind)) return [];
    if (typeof message.text !== 'string' || !message.text.trim()) return [];
    const text = role === 'user' ? userMessageDisplayText(message.text, prefix,
      {mergedSteer: message.display_kind === 'steer' || message.display_kind === 'pending_steer'}) : message.text;
    if (!text.trim()) return [];
    return [{key: `saved-${message.row_id ?? start + index}`, role, text,
      timestamp: typeof message.timestamp === 'number' ? message.timestamp : null,
      unprocessedCorrection: message.display_kind === 'pending_steer' && metadata?.delivered_to_model === false}];
  });
  if (liveTurn === null) return project(displayHistory);

  const submitted = liveTurn.user_row_id === undefined ? -1 : displayHistory.findIndex(message => message.row_id === liveTurn.user_row_id);
  const first = submitted !== -1 ? submitted : displayHistory.findIndex(message => typeof message.timestamp === 'number' && message.timestamp >= liveTurn.started_at);
  const boundary = first === -1 ? displayHistory.length : first;
  const current = project(displayHistory.slice(boundary), boundary);
  const timeline: Array<{offset: number; order: number; message: ConversationMessage}> = [];
  const cuts = new Set([0, liveTurn.assistant.length]);
  const corrections = liveTurn.corrections ?? [];
  const offsets = liveTurn.correction_offsets ?? [];
  if (corrections.length !== offsets.length || offsets.some((offset, index) => offset > liveTurn.assistant.length || index > 0 && offset < offsets[index - 1]!)) {
    throw new Error('The native live reply has invalid instruction positions.');
  }
  const origin = userMessageDisplayText(liveTurn.user, prefix);
  const remaining = [...current];
  const takeUser = (text: string) => {
    const index = remaining.findIndex(message => message.role === 'user' && message.text === text);
    return index === -1 ? null : remaining.splice(index, 1)[0]!;
  };
  const liveMessage = (role: ConversationMessage['role'], text: string, key: string): ConversationMessage =>
    ({role, text, key: `live-${liveTurn.started_at}-${key}`,
      timestamp: role === 'user' && key === 'user' ? liveTurn.started_at : null, unprocessedCorrection: false});
  if (origin.trim() && !internalDisplayKinds.has(liveTurn.display_kind ?? '') && liveTurn.display_metadata?._eight_internal_notification !== true) {
    const submittedIndex = liveTurn.user_row_id === undefined ? -1 : remaining.findIndex(message => message.key === `saved-${liveTurn.user_row_id}`);
    const submittedMessage = submittedIndex === -1 ? null : remaining.splice(submittedIndex, 1)[0]!;
    timeline.push({offset: 0, order: -1, message: submittedMessage ?? takeUser(origin) ?? liveMessage('user', origin, 'user')});
  }
  corrections.forEach((correction, index) => {
    const offset = offsets[index]!;
    cuts.add(offset);
    const text = userMessageDisplayText(correction, prefix, {mergedSteer: true});
    timeline.push({offset, order: index, message: takeUser(text) ?? liveMessage('user', text, `correction-${index}`)});
  });
  let cursor = 0;
  for (const message of remaining) {
    if (message.role === 'assistant') {
      const position = liveTurn.assistant.indexOf(message.text.trim(), cursor);
      if (position !== -1) {
        // Saved interim replies and streamed text describe the same turn.
        // Use their boundaries, then render that text from the live snapshot once.
        cuts.add(position); cursor = position + message.text.trim().length; cuts.add(cursor);
        continue;
      }
    }
    // Non-streamed native replies and queued inputs retain their recorded place.
    cuts.add(cursor);
    timeline.push({offset: cursor, order: corrections.length + timeline.length, message});
  }
  const boundaries = [...cuts].sort((a, b) => a - b);
  boundaries.slice(0, -1).forEach((start, index) => {
    const end = boundaries[index + 1]!;
    const text = liveTurn.assistant.slice(start, end);
    if (text.trim()) timeline.push({offset: start, order: Number.MAX_SAFE_INTEGER,
      message: liveMessage('assistant', text, `assistant-${start}`)});
  });
  timeline.sort((a, b) => a.offset - b.offset || a.order - b.order);
  return [...project(displayHistory.slice(0, boundary)), ...timeline.map(item => item.message).filter(message => message.text.trim())];
}
